<template>
  <div class="plotchange">
    <h1>{{ $t('plotChangePage.title') }}</h1>
    <p>
      {{ $t('plotChangePage.description') }}
      <br />{{ $t('plotChangePage.descriptionSuffix') }}
    </p>
    <div class="plotchange-cards">
      <div class="plotchange-card">
        <div class="plotchange-card-title">
          <div class="mode-name-column">
            <div>{{ $t('plotChangePage.kishoutenketsuMode') }}</div>
          </div>
          <button v-if="currentMode === 'kishoutenketsu'" class="selected" disabled>{{ $t('plotChangePage.selected') }}</button>
          <button v-else class="change" @click="changePlotMode('kishoutenketsu')">{{ $t('plotChangePage.changeButton') }}</button>
        </div>
        <div class="plotchange-card-description">
          {{ $t('plotChangePage.kishoutenketsuDescription1') }}
          <br />{{ $t('plotChangePage.kishoutenketsuDescription2') }}
          <br />{{ $t('plotChangePage.kishoutenketsuDescription3') }}
        </div>
      </div>
      <div class="plotchange-card">
        <div class="plotchange-card-title">
          <div class="mode-name-column">
            <div>{{ $t('plotChangePage.johakyuMode') }}</div>
            <img src="@/assets/img/icon/premium.png" />
          </div>
          <button v-if="currentMode === 'johakyu'" class="selected" disabled>{{ $t('plotChangePage.selected') }}</button>
          <button v-else class="change" @click="changePlotMode('johakyu')">{{ $t('plotChangePage.changeButton') }}</button>
        </div>
        <div class="plotchange-card-description">
          {{ $t('plotChangePage.johakyuDescription1') }}
          <br />{{ $t('plotChangePage.johakyuDescription2') }}
          <br />{{ $t('plotChangePage.johakyuDescription3') }}
        </div>
      </div>
      <div class="plotchange-card">
        <div class="plotchange-card-title">
          <div class="mode-name-column">
            <div>{{ $t('plotChangePage.customMode') }}</div>
            <img src="@/assets/img/icon/premium.png" />
          </div>
          <button v-if="currentMode === 'custom'" class="selected" disabled>{{ $t('plotChangePage.selected') }}</button>
          <button v-else class="change" @click="changePlotMode('custom')">{{ $t('plotChangePage.changeButton') }}</button>
        </div>
        <div class="plotchange-card-description">
          {{ $t('plotChangePage.customDescription1') }}
          <br />{{ $t('plotChangePage.customDescription2') }}
          <br />{{ $t('plotChangePage.customDescription3') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { Mode, Plot, PlotGroups, Plots, SubPlot, UpdatePlotModePayload, UpdateSubPlotPayload } from "@/lib/models/plot";
import { Plan } from "@/lib/models/user";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { Dialog } from "@/lib/utils";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
// import { downloadPublicImage } from "@/lib/s3";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    novelId: String,
    plotId: String,
    plot: Object as PropType<Plot | SubPlot>,
  },
  computed: {
    currentMode() {
      const { mode } = this.$route.query;
      return mode as Mode;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
    convertPlot() {
      return (plots, mode) => {
        const originalPlot = { description: "", plots: [] as Plots[] };
        plots.plotGroups.forEach((plotArray) => {
          originalPlot.description += `【${plotArray.name}】\n${plotArray.description}\n`;
          plotArray.plots.forEach((plot) => {
            originalPlot.plots.push(plot);
          });
        });
        switch (mode) {
          case Mode.kishoutenketsu: {
            const KSTKArray: PlotGroups[] = [
              {
                name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ki') as string,
                description: originalPlot.description,
                plots: originalPlot.plots,
              },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.shou') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ten') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ketsu') as string, description: "", plots: [] },
            ];
            return KSTKArray;
          }
          case Mode.johakyu: {
            const JHQArray: PlotGroups[] = [
              {
                name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.jo') as string,
                description: originalPlot.description,
                plots: originalPlot.plots,
              },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.ha') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.kyu') as string, description: "", plots: [] },
            ];
            return JHQArray;
          }
          default:
            // customの場合を想定、何も変更しないで返す
            return plots.plotGroups;
        }
      };
    },
  },
  methods: {
    async changePlotMode(mode) {
      const { $store, $router, novelId, plotId, plot, isFree, convertPlot } = this;
      const { dispatch } = $store;

      if (isFree && mode !== Mode.kishoutenketsu) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "プロットのモード変更はプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくとプロットを『序破急』や『起承転承転結』などのお好きな形式に変更ができます。より複雑なストーリーの組み立てや長編作品・シリーズ作品の管理にご活用ください。",
        //   image: (await downloadPublicImage("shared/images/gif/plot_mode_change.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        $router.push({ name: "subscriptionAnnounce", query: { from: "changePlotMode" } });

        return;
      }

      const plotGroups = convertPlot(plot, mode);

      if (plotId) {
        const updateSubPlotPayload: UpdateSubPlotPayload = {
          novelId,
          plotId,
          name: (plot as SubPlot).name,
          plotGroups,
          mode,
          layout: plot.layout,
          displayFormat: plot.displayFormat,
        };
        dispatch("plotModule/updateSubPlot", updateSubPlotPayload);
      } else {
        const updatePlotModePayload: UpdatePlotModePayload = {
          novelId,
          plotGroups,
          mode,
          layout: plot.layout,
          displayFormat: plot.displayFormat,
        };
        dispatch("plotModule/updatePlotMode", updatePlotModePayload);
      }

      const pageName = mode === Mode.custom ? "plotEdit" : "plot";
      if (plotId) {
        $router.push({ name: pageName, params: { novelId, plotId } });
      } else {
        $router.push({ name: pageName, params: { novelId } });
      }
    },
  },
});

interface Props {
  novelId: string;
  plotId?: string;
  plot: Plot | SubPlot;
}

interface Data {}

interface Computed {
  currentMode: Mode;
  plan: Plan;
  isFree: boolean;
  convertPlot(plots: Plot, mode: Mode): PlotGroups[];
}

interface Methods {
  changePlotMode(mode: Mode): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.plotchange {
  padding: 50px 30px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  h1 {
    margin: 15px 0;
    text-align: center;
    color: var(--color-text-normal);
  }

  p {
    margin: 0px;
    margin-bottom: 20px;
    text-align: center;
    font-size: var(--font-size-md);
    color: var(--color-text-normal);
  }

  &-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    margin-top: 20px;
  }
}

.plotchange-card {
  box-shadow: var(--shadow-sm);
  padding: 0px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  background-color: var(--color-background-normal);
}

.plotchange-card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - 30px);
  background-color: var(--color-surface-subtle);
  padding: 10px 15px;
  margin: 15px 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);

  .mode-name-column {
    display: flex;
    align-items: center;
    flex: 1;
    padding-right: 10px;

    div {
      line-height: 1.4;
    }

    img {
      width: 1.5rem;
      height: 1.5rem;
      margin-left: 5px;
      flex-shrink: 0;
    }
  }

  button {
    padding: 7px 10px;
    border: none;
    color: var(--color-text-on-fixed-bg);
    border-radius: var(--radius-xs);
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-xs);
    cursor: pointer;
    transition: all 0.3s ease 0s;
    flex-shrink: 0;

    &.change {
      background-color: var(--color-primary);

      &:hover {
        box-shadow: var(--shadow-sm);
        opacity: 1;
      }
    }

    &.selected {
      background-color: var(--color-warning);
      cursor: auto;
    }
  }
}

.plotchange-card-description {
  width: calc(100% - 30px);
  padding: 5px 15px 15px 15px;
  margin: 15px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

@media screen and (max-width: 1175px) {
  .plotchange {
    padding: 30px 20px;

    h1 {
      font-size: var(--font-size-md);
    }

    p {
      font-size: var(--font-size-xs);
      margin-bottom: 15px;
    }

    &-cards {
      grid-template-columns: 1fr;
      gap: 15px;
    }
  }
}
</style>
